import type { NextPage } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import React, { useEffect, useRef } from 'react'
import Contact from '../../components/contact/Contact'
import Item from '../../components/item/Item'
import ImageQuote from '../../components/imagequote/ImageQuote'
import CustomImage from '../../components/customImage/CustomImage'
import Parallax from '../../components/parallax/Parallax'

import styles from '../../styles/pages/Industries.module.scss'

import CustomMetaData from '../../components/general/customMetaData/CustomMetaData'

import { GenericPageLayoutProps } from '../../components/general/genericPageLayout/GenericPageLayout'

import { useGlobalContentContext } from '../../context/GlobalContentContext'
import { useIsMobile } from '../../hooks/useIsDesktop'
import { parseGenericPage } from '../../utils/parsers'
import { getAPI, createMarkup } from '../../utils/helpers'

type Props = {} & GenericPageLayoutProps

const Industries = (props: Props) => {
  const { setData } = useGlobalContentContext()

  useEffect(() => {
    setData(props.globalData)
  }, [props.globalData, setData])

  const isMobile = useIsMobile()

  return (
    <div className={styles.container}>
      <CustomMetaData
        title={props.originalResponse.meta_title}
        description={props.originalResponse.meta_description}
        image={props.originalResponse.meta_image}
      />

      <div className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroContentContainer}>
            <h1 className={styles.heroTitle}>
              {props.originalResponse.hero.title}
              <br />
              {props.originalResponse.hero.sub_title}
            </h1>
          </div>
          <div className={styles.heroImage}>
            <CustomImage
              src={props.originalResponse.hero}
              layout="fill"
              objectFit="cover"
            />
          </div>
        </section>

        <section className={styles.aboutSection}>
          <div className={styles.maxWidthContainer}>
            <h2
              className={styles.aboutSectionTitle}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.about.title
              )}
            ></h2>
            <div
              className={styles.aboutSectionDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.about.detail
              )}
            ></div>
          </div>
        </section>

        <section className={styles.aboutBlocksSection}>
          <div className={styles.linkSection}>
            <Link href="/technology">
              <a className={styles.link}>
                <span className={styles.linkArrow}>
                  <Image src={'/arrow-black.svg'} layout="fill" alt="" />
                </span>
                <span className={styles.linkText}>Learn about sakuu cells</span>
              </a>
            </Link>
          </div>
          {props.originalResponse.about_section_blocks.map(
            (block: any, index: number) => (
              <div key={index} className={styles.aboutBlocksSectionBlock}>
                <h2 className={styles.aboutBlocksSectionTitle}>
                  {block.title}
                </h2>
                <div className={styles.aboutBlocksSectionContent}>
                  <div className={styles.aboutBlocksSectionTags}>
                    {block.tags.split('  ').map((tag: any, index: number) => (
                      <React.Fragment key={index}>
                        <span>{tag}</span>
                        {(index == 3 ||
                          (block.tags.split('  ').length < 4 &&
                            block.tags.split('  ').length - 1 == index)) &&
                          isMobile && (
                            <div
                              className={styles.aboutBlocksSectionTagsMore}
                            ></div>
                          )}
                      </React.Fragment>
                    ))}
                  </div>
                  <Parallax >
                    <CustomImage src={block} layout="responsive" />
                  </Parallax>
                </div>
                <div
                  className={styles.aboutBlocksSectionDetail}
                  dangerouslySetInnerHTML={createMarkup(block.detail)}
                ></div>
              </div>
            )
          )}
        </section>

        <Contact />
      </div>
    </div>
  )
}

export async function getStaticProps() {
  const page: GenericPageLayoutProps = await parseGenericPage(
    `${getAPI()}/industriespage/`
  )
  return {
    props: {
      originalResponse: page.originalResponse,
      title: page.title,
      globalData: page.globalData,
      meta_title: page.meta_title,
      meta_description: page.meta_description,
    },
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS,
  }
}

export const NEXTJS_REVALIDATE_PAGE_IN_SECONDS = 10
export default Industries
